<template>
  <v-card elevation="0" class="pa-4">
    <v-col cols="12" md="12">
       <h1 class="mb-2 mr-4">Pedidos de cotação</h1>
      <v-card outlined>
        <v-card-title class="text-h6 font-weight-bold d-flex align-center justify-space-between flex-wrap">
          <div class="d-flex align-center justify-center flex-wrap">
            <v-text-field
              v-model="searchText"
              dense
              outlined
              hide-details
              label="Buscar (número, cliente, CNPJ ou agência)"
              prepend-inner-icon="mdi-magnify"
              clearable
              color="primary"
              style="min-width: 410px;"
              class="mr-2 primary--text">
            </v-text-field>
            <v-btn-toggle style="margin-bottom: 5px" v-model="selectedPeriod" mandatory dense color="primary" class="mr-2">
              <v-btn small value="today">Hoje</v-btn>
              <v-btn small value="7days">7 dias</v-btn>
              <v-btn small value="30days">30 dias</v-btn>
              <v-btn small value="all">Todos</v-btn>
            </v-btn-toggle>
            <v-menu
              ref="dateRangeMenu"
              v-model="dateRangeMenu"
              :close-on-content-click="false"
              transition="scale-transition"
              offset-y
              min-width="auto">
              <template v-slot:activator="{ on, attrs }">
                <v-text-field
                  v-model="dateRangeText"
                  dense
                  outlined
                  readonly
                  hide-details
                  prepend-inner-icon="mdi-calendar"
                  color="primary"
                  label="Data"
                  v-bind="attrs"
                  v-on="on"
                  style="min-width: 260px;"
                  class="ml-2 primary--text">
                </v-text-field>
              </template>
              <v-card>
                <v-card-text class="pb-0">
                  <div class="text-caption grey--text text--darken-1 mb-2 text-center">
                    Selecione data de início e fim
                  </div>
                </v-card-text>
                <v-date-picker
                  v-model="dateRange"
                  range
                  no-title
                  scrollable
                  locale="pt-BR"
                  color="primary"
                  @change="onDateRangeChange">
                  <v-spacer></v-spacer>
                  <v-btn text color="primary" @click="dateRangeMenu = false">Cancelar</v-btn>
                  <v-btn text color="primary" @click="applyDateRange">OK</v-btn>
                </v-date-picker>
              </v-card>
            </v-menu>
            <v-select
              v-model="selectedStatus"
              :items="statusOptions"
              item-text="text"
              item-value="value"
              dense
              outlined
              hide-details
              prepend-inner-icon="mdi-filter-outline"
              color="primary"
              label="Status"
              style="max-width: 200px;"
              class="ml-2 primary--text">
            </v-select>
            <v-btn
              :disabled="!hasFilterChanges"
              :color="hasFilterChanges ? 'primary' : 'grey'"
              @click="applyFilters"
              small
              style="margin-bottom: 5px"
              class="ml-2">
              Aplicar Filtros
            </v-btn>
            <v-btn
              @click="clearFilters"
              color="red"
              dark
              small
              style="margin-bottom: 5px"
              class="ml-2">
              Limpar Filtros
            </v-btn>
          </div>
          <span class="d-flex align-center">
            Total: {{ filteredNegotiations.length }}
          </span>
        </v-card-title>

        <v-data-table 
          :headers="headers" 
          :items="filteredNegotiations" 
          :loading="negotiationsLoading"
          :items-per-page="negotiationsPerPage" 
          :sort-by="['gridVersionCreatedOn']" 
          :sort-desc="[true]" 
          class="elevation-0" 
          loading-text="Carregando pedidos...">
          
          <template v-slot:[`item.gridId`]="{ item }">
            <div class="text-center">#{{ item.gridId }}</div>
          </template>

          <template v-slot:[`item.orderStatus`]="{ item }">
            <div class="text-center">
              <v-chip 
                :color="getOrderStatusColor(item.orders, item)" 
                :text-color="getOrderStatusTextColor(item.orders, item)" 
                small>
                <strong>{{ getOrderStatusId(item.orders, item) }}</strong>
              </v-chip>
            </div>
          </template>

          <template v-slot:[`item.profileLogo`]="{ item }">
            <v-img v-if="item.profileLogo" 
                   :lazy-src="item.profileLogo" 
                   max-height="41" 
                   max-width="41" 
                   :src="item.profileLogo"
                   class="logo-image">
            </v-img>
            <div class="d-flex justify-center" v-else>-</div>
          </template>

          <template v-slot:[`item.profileDisplayName`]="{ item }">
            <div class="d-flex align-center justify-center">
              <span>{{ item.profileDisplayName }}</span>
              <v-menu 
                v-model="item.showTooltip"
                offset-y
                close-on-click
                close-on-content-click
                max-width="400">
                <template v-slot:activator="{ on, attrs }">
                  <v-btn 
                    icon 
                    small 
                    class="ml-2" 
                    color="primary"
                    v-bind="attrs"
                    v-on="on">
                    <v-icon small>mdi-information-outline</v-icon>
                  </v-btn>
                </template>
                <v-card>
                  <v-card-title class="text-body-1 font-weight-bold pa-3 pb-2 d-flex justify-space-between align-center">
                    <span class="mr-3">{{ item.profileDisplayName }}</span>
                    <v-btn 
                      icon 
                      x-small 
                      @click="item.showTooltip = false">
                      <v-icon small>mdi-close</v-icon>
                    </v-btn>
                  </v-card-title>
                  <v-card-text class="pa-3 pt-0">
                    <div class="text-body-2">
                      <strong>Razão Social:</strong> {{ item.profileName }}
                    </div>
                    <div class="text-body-2 mt-1">
                      <strong>CNPJ:</strong> {{ formatCNPJ(item.profileDocument) }}
                    </div>
                  </v-card-text>
                </v-card>
              </v-menu>
            </div>
          </template>

          <template v-slot:[`item.profileName`]="{ item }">
            <div class="text-center">{{ item.profileName }}</div>
          </template>
          
          <template v-slot:[`item.agencyDisplayName`]="{ item }">
            <div class="text-center">
              <v-chip v-if="item.agencyDisplayName" color="#7F9EBB" text-color="white" small>
                <strong>{{ item.agencyDisplayName }}</strong>
              </v-chip>
              <v-chip v-else color="#fb6e52" text-color="white" small>
                <strong>Canal do Anúncio</strong>
              </v-chip>
            </div>
          </template>

          <template v-slot:[`item.profileDocument`]="{ item }">
            <div class="text-center">{{ formatCNPJ(item.profileDocument) }}</div>
          </template>

          <template v-slot:[`item.gridVersionCreatedOn`]="{ item }">
            <div class="text-center">{{ formatDate(item.gridVersionCreatedOn) }}</div>
          </template>

          <template v-slot:[`item.gridFirstDate`]="{ item }">
            <div class="text-center">{{ formatDate(item.gridFirstDate) }}</div>
          </template>

          <template v-slot:[`item.gridUpdatedOn`]="{ item }">
            <div class="text-center">
              <div v-if="item.gridUpdatedOn">
                <div class="text-body-2 font-weight-medium">{{ formatDateTime(item.gridUpdatedOn) }}</div>
                <div class="text-caption grey--text">{{ formatUserEmail(item.gridUpdatedByEmail) }}</div>
              </div>
              <v-chip v-else color="grey lighten-2" x-small>
                <v-icon x-small left>mdi-clock-outline</v-icon>
                Sem atualização
              </v-chip>
            </div>
          </template>

          <template v-slot:[`item.orders`]="{ item }"> 
            <div class="text-center">
              <v-menu 
                v-model="item.showOrdersTooltip"
                offset-y
                close-on-click
                close-on-content-click
                max-width="400">
                <template v-slot:activator="{ on, attrs }">
                  <v-chip color="primary" text-color="white" small v-bind="attrs" v-on="on">
                    {{ item.orders.length }}
                  </v-chip>
                </template>
                <v-card>
                  <v-card-title class="text-body-1 font-weight-bold pa-3 pb-2 d-flex justify-space-between align-center">
                    <span class="mr-3">Inventários</span>
                    <v-btn icon x-small @click="item.showOrdersTooltip = false">
                      <v-icon small>mdi-close</v-icon>
                    </v-btn>
                  </v-card-title>
                  <v-card-text class="pa-3 pt-0">
                    <div class="text-body-2">
                      <ul style="padding-left: 1em; margin: 0;"> 
                        <li v-for="order in item.orders" :key="order.orderSyncId" style="font-size: 0.95em; margin-bottom: 4px;">
                          {{ order.inventoryDisplayName || 'Sem nome' }}
                        </li>
                      </ul>
                    </div>
                  </v-card-text>
                </v-card>
              </v-menu>
            </div>
          </template>

       
           <template v-slot:[`item.actions`]="{ item }">
                <div class="text-center">
                  <v-btn icon :to="`/app/comercial/cotacoes/${item.gridId}`"><v-icon small>mdi-arrow-right</v-icon></v-btn>
                </div>
              </template>

        </v-data-table>
      </v-card>
    </v-col>
  </v-card>
</template>

<script>
import http from 'axios';
import helper from '@/plugins/helper'
import { getAuth } from '@/services/authService'
export default {
  name: 'QuotationsList',
  
  data() {
    return {

      get auth() {
        return getAuth()
      },

      negotiations: [],
      negotiationsLoading: false,
      negotiationsPerPage: 10,
      searchText: '',
      selectedPeriod: 'all',
      dateRange: [],
      dateRangeMenu: false,
      customDateRange: null,
      selectedStatus: -1,
      appliedPeriod: 'all',
      appliedCustomDateRange: null,
      appliedStatus: -1,
      statusOptions: [
        { text: 'Todos', value: -1 },
        { text: 'Cotação', value: 2 },
        { text: 'Em aprovação', value: 3 },
        { text: 'Reserva', value: 6 },
        { text: 'Conteúdo', value: 4 },
        { text: 'Veiculação', value: 5 },
        { text: 'Cancelado', value: 0 }
      ],
      headers: [
        { text: 'Número do plano', value: 'gridId', width: '90px', align: 'center' },
        { text: 'Cliente', value: 'profileLogo', width: '80px', align: 'center' },
        { text: 'Nome', value: 'profileDisplayName', align: 'center' },
        { text: 'Agência', value: 'agencyDisplayName', width: '90px', align: 'center' },
        { text: 'Data da cotação', value: 'gridVersionCreatedOn', sortable: true,width: '115px', align: 'center' },
        { text: 'Formatos', value: 'orders', sortable: false, width: '90px', align: 'center' },
        { text: 'Início planejado da campanha', value: 'gridFirstDate',width: '135px', sortable: false, align: 'center' },
        { text: 'Última atualização', value: 'gridUpdatedOn', width: '180px', sortable: true, align: 'center' },
        { text: 'Status', value: 'orderStatus',  align: 'center' },
        { text: 'Ver', value: 'actions', sortable: false, width: '60px', align: 'center' }
      ]
    };
  },

  computed: {
    dateRangeText() {
      if (this.customDateRange) {
        const [from, to] = this.customDateRange;
        return `${this.formatDate(from)} - ${this.formatDate(to)}`;
      }
      return this.getPeriodText();
    },
    hasFilterChanges() {
      return (
        this.selectedPeriod !== this.appliedPeriod ||
        this.selectedStatus !== this.appliedStatus ||
        JSON.stringify(this.customDateRange) !== JSON.stringify(this.appliedCustomDateRange)
      );
    },
    filteredNegotiations() {
      let filtered = [...this.negotiations];

      
      const { from, to } = this.getAppliedDateRange();
      filtered = filtered.filter(item => {
        if (!item.gridVersionCreatedOn) return false;
        const itemDate = item.gridVersionCreatedOn.split(' ')[0]; 
        return itemDate >= from && itemDate <= to;
      });

      if (this.appliedStatus !== -1) {
        filtered = filtered.filter(item => {
          const statusIdList = item.orders.map(order => order.orderStatusId);
          const minStatusId = statusIdList.length > 0 ? Math.min(...statusIdList) : null;
          
          const statusMapping = {
            1: [14],
            2: [15, 16],
            3: [18, 19, 20, 32, 33],
            6: [28, 29, 30, 31],
            4: [21, 22, 23],
            5: [24, 25, 26, 27],
            0: [13, 17]
          };
          
          const validStatusIds = statusMapping[this.appliedStatus] || [];
          return validStatusIds.includes(minStatusId);
        });
      }

      if (this.searchText && this.searchText.trim()) {
        const search = this.searchText.toLowerCase().trim();
        filtered = filtered.filter(item => {
          return (
            item.gridId?.toString().includes(search) ||
            item.profileDisplayName?.toLowerCase().includes(search) ||
            item.profileName?.toLowerCase().includes(search) ||
            item.profileDocument?.includes(search) ||
            item.agencyDisplayName?.toLowerCase().includes(search) ||
            'canal do anúncio'.includes(search) && !item.agencyDisplayName
          );
        });
      }

      return filtered;
    }
  },

  watch: {
    selectedPeriod(newValue) {
      if (newValue !== 'custom') {
        this.customDateRange = null;
        this.dateRange = [];
      }
    }
  },

  mounted() {
    this.fetchTrades();
  },

  methods: {
    getPeriodText() {
      const periodMap = {
        'today': 'Hoje',
        '7days': 'Últimos 7 dias',
        '15days': 'Últimos 15 dias',
        '30days': 'Últimos 30 dias',
        'all': 'Todos os períodos'
      };
      return periodMap[this.selectedPeriod] || 'Selecione o período';
    },

    getDateRange() {
      const today = this.$moment().format('YYYY-MM-DD');
      
      if (this.customDateRange) {
        const [from, to] = this.customDateRange;
        return { from, to };
      }

      switch (this.selectedPeriod) {
        case 'today':
          return { from: today, to: today };
        case '7days':
          return { 
            from: this.$moment().subtract(7, 'days').format('YYYY-MM-DD'), 
            to: today 
          };
        case '15days':
          return { 
            from: this.$moment().subtract(15, 'days').format('YYYY-MM-DD'), 
            to: today 
          };
        case '30days':
          return { 
            from: this.$moment().subtract(30, 'days').format('YYYY-MM-DD'), 
            to: today 
          };
        case 'all':
        default:
          return { from: '2015-01-01', to: today };
      }
    },

    getAppliedDateRange() {
      const today = this.$moment().format('YYYY-MM-DD');
      
      if (this.appliedCustomDateRange) {
        const [from, to] = this.appliedCustomDateRange;
        return { from, to };
      }

      switch (this.appliedPeriod) {
        case 'today':
          return { from: today, to: today };
        case '7days':
          return { 
            from: this.$moment().subtract(7, 'days').format('YYYY-MM-DD'), 
            to: today 
          };
        case '15days':
          return { 
            from: this.$moment().subtract(15, 'days').format('YYYY-MM-DD'), 
            to: today 
          };
        case '30days':
          return { 
            from: this.$moment().subtract(30, 'days').format('YYYY-MM-DD'), 
            to: today 
          };
        case 'all':
        default:
          return { from: '2015-01-01', to: today };
      }
    },

    applyFilters() {
      this.appliedPeriod = this.selectedPeriod;
      this.appliedCustomDateRange = this.customDateRange ? [...this.customDateRange] : null;
      this.appliedStatus = this.selectedStatus;
    },

    clearFilters() {
      this.selectedPeriod = 'all';
      this.customDateRange = null;
      this.dateRange = [];
      this.selectedStatus = -1;
      
      this.appliedPeriod = 'all';
      this.appliedCustomDateRange = null;
      this.appliedStatus = -1;
    },

    onDateRangeChange(dates) {
      if (dates.length === 2) {
        this.dateRange = dates.sort();
      }
    },

    applyDateRange() {
      if (this.dateRange.length === 2) {
        this.customDateRange = this.dateRange.sort();
        this.selectedPeriod = 'all';
        this.dateRangeMenu = false;
      }
    },
    async fetchTrades() {
      this.negotiationsLoading = true;
      try {
        const today = this.$moment().format('YYYY-MM-DD');
        await this.$axios.get(`portal/orders?ignoreProfile=1&groupGrids=1&from=2015-01-01&to=${today}&cdv`).then(data => {
          const result = data.data.data || [];
          
          this.negotiations = result.map(item => ({
            gridId: item.gridId,
            profileLogo: item.profileLogo,
            profileDisplayName: item.profileDisplayName,
            profileName: item.profileName,
            profileDocument: item.profileDocument,
            agencyDisplayName: item.agencyDisplayName,
            gridVersionCreatedOn: item.gridVersionCreatedOn,
            gridFirstDate: item.gridFirstDate,
            gridUpdatedOn: item.gridUpdatedOn,
            gridUpdatedByEmail: item.gridUpdatedByEmail,
            expired: item.expired,
            orders: item.orders,
            showTooltip: false,
            showOrdersTooltip: false
          }));
        });
      } catch (error) {
        console.error('Erro ao buscar cotações:', error);
      } finally {
        this.negotiationsLoading = false;
      }
    },

    toggleTooltip(item) {
      
      this.negotiations.forEach(negotiation => {
        if (negotiation !== item) {
          negotiation.showTooltip = false;
        }
      });
      
      item.showTooltip = !item.showTooltip;
    },

    toggleOrdersTooltip(item) {
      this.negotiations.forEach(negotiation => {
        if (negotiation !== item) {
          negotiation.showOrdersTooltip = false;
        }
      });

      item.showOrdersTooltip = !item.showOrdersTooltip;
    },

    formatCNPJ(cnpj) {
      if (!cnpj) return '';
      const cleanCNPJ = cnpj.toString().replace(/\D/g, '');
      if (cleanCNPJ.length !== 14) return '';
      return cleanCNPJ.replace(
        /^(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})$/,
        '$1.$2.$3/$4-$5'
      );
    },

    formatDate(dateString) {
      if (!dateString) return '';
      
      
      if (dateString.includes(' ')) {
        const [datePart] = dateString.split(' ');
        const [year, month, day] = datePart.split('-');
        return `${day}/${month}/${year}`;
      }
      
      
      if (dateString.includes('-')) {
        const [year, month, day] = dateString.split('-');
        return `${day}/${month}/${year}`;
      }
      
      return dateString;
    },

    formatDateTime(dateTimeString) {
      if (!dateTimeString) return '';
      
      const [datePart, timePart] = dateTimeString.split(' ');
      const [year, month, day] = datePart.split('-');
      const [hour, minute] = timePart.split(':');
      
      return `${day}/${month}/${year} às ${hour}:${minute}`;
    },

    formatUserEmail(email) {
      if (!email) return 'Usuário desconhecido';
      
      const username = email.split('@')[0];
      return username.charAt(0).toUpperCase() + username.slice(1);
    },

    getOrderStatusId(orders, item){
      const statusIdList = []

      orders.forEach(order => {
        statusIdList.push(order.orderStatusId)
      });

      const minStatusId = statusIdList.length > 0 ? Math.min(...statusIdList) : null;

      if (item.expired === true && minStatusId >= 14 && minStatusId <= 24) {
        return 'Cancelada por falta de resposta'
      }

      return helper.getStatusText(minStatusId)
    },

    getOrderStatusColor(orders, item) {
      const statusIdList = []

      orders.forEach(order => {
        statusIdList.push(order.orderStatusId)
      });

      const minStatusId = statusIdList.length > 0 ? Math.min(...statusIdList) : null;
      if (item.expired === true && minStatusId >= 14 && minStatusId <= 24) {
        return '#dc3545'
      }

      return helper.getStatusColor(minStatusId);
    },

    getOrderStatusTextColor(orders, item) {
      const statusIdList = []

      orders.forEach(order => {
        statusIdList.push(order.orderStatusId)
      });

      const minStatusId = statusIdList.length > 0 ? Math.min(...statusIdList) : null;
      
      if (item.expired === true && minStatusId >= 14 && minStatusId <= 24) {
        return 'white';
      }
      
      const yellowBackgroundStatusIds = [14, 15, 16, 22, 26, 28, 31];
      
      if (minStatusId && yellowBackgroundStatusIds.includes(minStatusId)) {
        return 'black';
      }
      
      return 'white';
    }
  }
};
</script>

<style scoped>
* {
  font-family: 'Roboto', sans-serif !important;
}

.v-data-table>>>.v-data-table__wrapper {
  overflow: visible;
}

.text-caption {
  font-size: 0.75rem !important;
}

.v-chip.v-size--small {
  height: 20px;
  font-size: 10px;
  min-width: 20px;
}

.logo-image {
  object-fit: contain;
}

.v-data-table * {
  font-family: 'Roboto', sans-serif !important;
}

.theme--light.v-sheet {
  border-color: #dddddd !important;
}

.primary--text >>> input {
  color: var(--v-primary-base) !important;
}

.primary--text >>> .v-select__selection {
  color: var(--v-primary-base) !important;
}
</style>